export type StarterProfileStepId = "style" | "preferences" | "sizes" | "budget";

export interface StarterProfileStep {
    id: StarterProfileStepId;
    label: string;
    title: string;
    description: string;
    pageIndexes: number[];
    questionIds: number[];
}

export type StarterProfileAnswer = string | number | Array<string | number> | null | undefined;
export type StarterProfileAnswers = Record<number, StarterProfileAnswer>;

export const STARTER_PROFILE_STEPS: StarterProfileStep[] = [
    {
        id: "style",
        label: "Twój styl",
        title: "Jaki styl jest Ci najbliższy?",
        description: "Wybierz styl, który najlepiej opisuje rzeczy, po które sięgasz najchętniej.",
        pageIndexes: [22],
        questionIds: [27],
    },
    {
        id: "preferences",
        label: "Twoje preferencje",
        title: "Co lubisz nosić?",
        description: "Wybierz ulubione rodzaje ubrań i kolory. Możesz zaznaczyć więcej niż jedną odpowiedź.",
        pageIndexes: [16, 18],
        questionIds: [21, 40, 23],
    },
    {
        id: "sizes",
        label: "Twoje rozmiary",
        title: "Jakie rozmiary wybierasz najczęściej?",
        description: "Podaj osobno rozmiar góry, dołu i sukienek. Zawsze możesz je później zmienić.",
        pageIndexes: [5, 6, 7],
        questionIds: [11, 12, 13],
    },
    {
        id: "budget",
        label: "Twój budżet",
        title: "W jakim budżecie mamy szukać?",
        description: "Ustaw wygodny zakres cen dla poszczególnych elementów stylizacji.",
        pageIndexes: [29],
        questionIds: [34, 36, 37, 38],
    },
];

const CLOTHING_PREFERENCE = {
    pants: "36",
    dresses: "37",
    mix: "38",
} as const;

export function getStarterClothingPreference(
    surveyAnswer: string | number | null | undefined
): string[] {
    const value = String(surveyAnswer ?? "");
    if (value === CLOTHING_PREFERENCE.mix) {
        return [CLOTHING_PREFERENCE.pants, CLOTHING_PREFERENCE.dresses];
    }
    return value === CLOTHING_PREFERENCE.pants || value === CLOTHING_PREFERENCE.dresses
        ? [value]
        : [];
}

export function getSurveyClothingPreference(
    starterAnswers: Array<string | number>
): string {
    const selected = new Set(starterAnswers.map(String));
    if (selected.has(CLOTHING_PREFERENCE.pants) && selected.has(CLOTHING_PREFERENCE.dresses)) {
        return CLOTHING_PREFERENCE.mix;
    }
    if (selected.has(CLOTHING_PREFERENCE.pants)) return CLOTHING_PREFERENCE.pants;
    if (selected.has(CLOTHING_PREFERENCE.dresses)) return CLOTHING_PREFERENCE.dresses;
    return "";
}

const NO_COLOR_PREFERENCE = "BRAK PREFERENCJI";

export interface StarterColorOption {
    id?: string | number;
    value?: string | number;
    label: string;
    imgUrl?: string | null;
}

export interface StarterColorPaletteItem {
    key: string;
    label: string;
    imgUrl?: string | null;
    likedOptionId: string | number;
    dislikedOptionId: string | number;
}

const normalizeColorLabel = (label: string): string => label.trim().toLocaleUpperCase("pl-PL");
const getColorOptionId = (option: StarterColorOption): string | number => option.id ?? option.value!;

export function getStarterColorPalette(
    likedOptions: StarterColorOption[],
    dislikedOptions: StarterColorOption[]
): StarterColorPaletteItem[] {
    const likedByLabel = new Map(
        likedOptions.map((option) => [normalizeColorLabel(option.label), option])
    );

    return dislikedOptions.reduce<StarterColorPaletteItem[]>((palette, dislikedOption) => {
        const key = normalizeColorLabel(dislikedOption.label);
        const likedOption = likedByLabel.get(key);
        if (key === NO_COLOR_PREFERENCE || !likedOption) return palette;
        palette.push({
            key,
            label: dislikedOption.label,
            imgUrl: dislikedOption.imgUrl || likedOption.imgUrl,
            likedOptionId: getColorOptionId(likedOption),
            dislikedOptionId: getColorOptionId(dislikedOption),
        });
        return palette;
    }, []);
}

export type StarterColorPreference = "liked" | "disliked";

const containsOption = (
    answers: Array<string | number>,
    optionId: string | number
): boolean => answers.some((answer) => String(answer) === String(optionId));

const withoutOption = (
    answers: Array<string | number>,
    optionId: string | number
): Array<string | number> => answers.filter((answer) => String(answer) !== String(optionId));

export function toggleStarterColorPreference(
    likedAnswers: Array<string | number>,
    dislikedAnswers: Array<string | number>,
    color: Pick<StarterColorPaletteItem, "likedOptionId" | "dislikedOptionId">,
    preference: StarterColorPreference,
    noPreference?: Pick<StarterColorPaletteItem, "likedOptionId" | "dislikedOptionId">
): { likedAnswers: Array<string | number>; dislikedAnswers: Array<string | number> } {
    const selectingLiked = preference === "liked";
    const rawSelectedAnswers = selectingLiked ? likedAnswers : dislikedAnswers;
    const noPreferenceOptionId = noPreference
        ? selectingLiked
            ? noPreference.likedOptionId
            : noPreference.dislikedOptionId
        : undefined;
    const selectedAnswers = noPreferenceOptionId === undefined
        ? rawSelectedAnswers
        : withoutOption(rawSelectedAnswers, noPreferenceOptionId);
    const selectedOptionId = selectingLiked ? color.likedOptionId : color.dislikedOptionId;
    const nextSelectedAnswers = containsOption(selectedAnswers, selectedOptionId)
        ? withoutOption(selectedAnswers, selectedOptionId)
        : [...selectedAnswers, selectedOptionId];

    return selectingLiked
        ? {
            likedAnswers: nextSelectedAnswers,
            dislikedAnswers: withoutOption(dislikedAnswers, color.dislikedOptionId),
        }
        : {
            likedAnswers: withoutOption(likedAnswers, color.likedOptionId),
            dislikedAnswers: nextSelectedAnswers,
        };
}

export function hasStarterProfileAnswer(value: StarterProfileAnswer): boolean {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim().length > 0;
    return typeof value === "number" && Number.isFinite(value);
}

export function isStarterProfileStepComplete(
    step: StarterProfileStep,
    answers: StarterProfileAnswers
): boolean {
    return step.questionIds.every((questionId) => hasStarterProfileAnswer(answers[questionId]));
}

export function getFirstIncompleteStarterStep(answers: StarterProfileAnswers): number {
    const index = STARTER_PROFILE_STEPS.findIndex(
        (step) => !isStarterProfileStepComplete(step, answers)
    );
    return index === -1 ? STARTER_PROFILE_STEPS.length : index;
}

export type StarterProfileEntryMode = "intro" | "form";

export const getStarterProfileEntryMode = (search = ""): StarterProfileEntryMode => {
    const params = new URLSearchParams(search);
    return params.get("start") === "1" || params.get("edycja") === "1" ? "form" : "intro";
};

export function getStarterProfileInitialStep(
    answers: StarterProfileAnswers,
    isEditing: boolean
): number {
    return isEditing ? 0 : getFirstIncompleteStarterStep(answers);
}

export const getStarterProfileProgress = (answers: StarterProfileAnswers): number => {
    const completedSteps = STARTER_PROFILE_STEPS.filter((step) =>
        isStarterProfileStepComplete(step, answers)
    ).length;
    return Math.round((completedSteps / STARTER_PROFILE_STEPS.length) * 100);
};

export type StarterProfileSessionAction = "none" | "check" | "create";

export const getStarterProfileSessionAction = (
    activeSessionId?: number | null,
    latestUserSessionId?: number | null
): StarterProfileSessionAction => {
    if (activeSessionId) return "none";
    return latestUserSessionId ? "check" : "create";
};

export const getStarterProfileCompletionPath = (
    isEditing: boolean,
    isLastStep: boolean,
    sessionId?: number | null
): string | null => isEditing && isLastStep && sessionId
    ? `/konto/ankieta/${sessionId}`
    : null;

export const getNextMissingStarterProfilePage = (
    requiredPageIndexes: number[],
    pageQuestions: Record<number, unknown[] | null | undefined>
): number | null => requiredPageIndexes.find((index) => !Array.isArray(pageQuestions[index])) ?? null;

export const canLoadStarterProfilePages = (
    sessionId: number | null | undefined,
    userApiStatus: string | null | undefined
): boolean => Boolean(sessionId) && userApiStatus === "success";
